import { hmac } from "@noble/hashes/hmac.js";
import { sha256 } from "@noble/hashes/sha256.js";
import { randomBytes } from "@noble/hashes/utils.js";
import { CrdtState } from "../crdt/merge.js";
import { deriveKey } from "../crypto/hkdf.js";
import { deriveSyncKey } from "../crypto/keys.js";
import { decrypt, encrypt } from "../crypto/xchacha20poly1305.js";

const RECOVERY_KDF_INFO = "trustii-recovery-backup-v1";
const RECOVERY_VERIFICATION_DATA = "trustii-backup-v1";
const RECOVERY_KEY_HINT = "xchacha20poly1305-hkdf-v1";

export interface RecoveryBackupPlaintext {
  v: 1;
  account_root_key: string;
  crdt_snapshot?: CrdtState;
}

export function buildRecoveryBackupPayload(
  accountRootKey: Uint8Array,
  crdtSnapshot?: CrdtState,
): RecoveryBackupPlaintext {
  const payload: RecoveryBackupPlaintext = {
    v: 1,
    account_root_key: Buffer.from(accountRootKey).toString("base64"),
  };
  if (crdtSnapshot) {
    payload.crdt_snapshot = crdtSnapshot;
  }
  return payload;
}

export function deriveRecoveryKey(passphrase: string, accountEmail: string): Uint8Array {
  return deriveKey(
    new TextEncoder().encode(passphrase),
    accountEmail.toLowerCase().trim(),
    RECOVERY_KDF_INFO,
  );
}

export function computeVerificationTag(derivedKey: Uint8Array): string {
  const tag = hmac(sha256, derivedKey, new TextEncoder().encode(RECOVERY_VERIFICATION_DATA));
  return Buffer.from(tag).toString("hex");
}

export function encryptRecoveryBackup(
  passphrase: string,
  accountEmail: string,
  plaintext: RecoveryBackupPlaintext,
): { ciphertext: string; nonce: string; verificationTag: string; keyDerivationHint: string } {
  const derivedKey = deriveRecoveryKey(passphrase, accountEmail);
  const encoded = new TextEncoder().encode(JSON.stringify(plaintext));
  const { ciphertext, nonce } = encrypt(derivedKey, encoded);
  return {
    ciphertext: Buffer.from(ciphertext).toString("base64"),
    nonce: Buffer.from(nonce).toString("base64"),
    verificationTag: computeVerificationTag(derivedKey),
    keyDerivationHint: RECOVERY_KEY_HINT,
  };
}

export function decryptRecoveryBackup(
  passphrase: string,
  accountEmail: string,
  ciphertextB64: string,
  nonceB64: string,
): RecoveryBackupPlaintext {
  const derivedKey = deriveRecoveryKey(passphrase, accountEmail);
  const ciphertext = Buffer.from(ciphertextB64, "base64");
  const nonce = Buffer.from(nonceB64, "base64");
  const plaintext = decrypt(derivedKey, ciphertext, nonce);
  const payload = JSON.parse(new TextDecoder().decode(plaintext)) as RecoveryBackupPlaintext;
  if (payload.v !== 1) {
    throw new Error("unsupported_recovery_backup_version");
  }
  return payload;
}

export function restoreSyncKeyFromBackup(
  plaintext: RecoveryBackupPlaintext,
  accountId: number | string,
): Uint8Array {
  const accountRootKey = Buffer.from(plaintext.account_root_key, "base64");
  if (accountRootKey.length !== 32) {
    throw new Error("invalid_account_root_key");
  }
  return deriveSyncKey(accountRootKey, accountId);
}

export function generateRecoveryNonce(): Uint8Array {
  return randomBytes(24);
}
